/**
 * KART OG KOORDINATER — KOI Del 2 (avgjørelse 1 og 2).
 *
 * =================================================================================================
 * KOORDINATER ER STRENGER PÅ TVERS AV LEDNINGEN, OG DET ER IKKE EN FORGLEMMELSE.
 *
 * Kolonnen er `numeric(9,6)`, og `pg` leverer en `numeric` som STRENG — med vilje, fordi en
 * `numeric` kan bære mer presisjon enn en IEEE-754-double kan holde. Å gjøre den til et tall her
 * ville betydd at `67.280000` reiser ut som `67.28` og kommer tilbake som `67.27999999999999` fra
 * en klient som regnet på den, og at to punkter som ER like blir ulike.
 *
 * Så: strengen bæres urørt hele veien, klienten gjør den til et tall NÅR den skal tegne — Leaflet
 * tar tall — og skriving går tilbake som streng. Det samme valget `economy.ts` gjør for beløp, og
 * av nøyaktig den samme grunnen.
 *
 * Formen valideres av et regulært uttrykk framfor av `z.coerce.number()`: «67,28» med komma og
 * «67.2800001» med sju desimaler er begge ting en klient kan finne på å sende, og begge skal være
 * en 400 som navngir feltet.
 *
 * =================================================================================================
 * INGEN AVSTAND, INGEN POLYGONER, INGEN RUTER (avgjørelse 2). Et punkt er et punkt. «Nærmeste
 * ledige bil» er PostGIS og en egen ordre; `RESSURSER-ANALYSE.md` har den allerede oppe som
 * manglende data.
 */
import { z } from 'zod';

import { ACCREDITATION_COLOURS } from './accreditation.js';

/**
 * Hva et punkt kan stå på (migrasjon 0222).
 *
 * `custom` er punktet som ikke står på noe: «Nødutgang», «Møteplass ved brannbil». Det er det
 * eneste som MÅ ha en egen etikett, og det eneste som ikke har et objekt å låne navnet sitt fra.
 */
/**
 * `event` KOM MED KOI DEL 3 (migrasjon 0226), og 0224 hadde allerede lovet den: en kalenderhendelse
 * bærer stedet sitt som fritekst OG som et punkt, akkurat som en oppgave gjør.
 */
export const MAP_OBJECT_TYPES = [
  'task',
  'resource',
  'stage',
  'staffing_role',
  'custom',
  'event',
] as const;
export type MapObjectType = (typeof MAP_OBJECT_TYPES)[number];
export const mapObjectTypeSchema = z.enum(MAP_OBJECT_TYPES);

/**
 * `-90.000000` til `90.000000`, som tekst. Se filhodet for hvorfor det ikke er et tall.
 *
 * Seks desimaler er taket fordi kolonnen har seks: en sjuende ville blitt rundet av basen, og en
 * klient som leste tilbake noe annet enn den skrev er en klient som tror den mistet noe.
 */
const latitudeSchema = z
  .string()
  .regex(/^-?\d{1,2}(\.\d{1,6})?$/, 'A latitude with at most six decimals.')
  .refine((value) => Math.abs(Number(value)) <= 90, 'A latitude is between -90 and 90.');

const longitudeSchema = z
  .string()
  .regex(/^-?\d{1,3}(\.\d{1,6})?$/, 'A longitude with at most six decimals.')
  .refine((value) => Math.abs(Number(value)) <= 180, 'A longitude is between -180 and 180.');

/** Leaflets egen skala: 0 er hele kloden, 19 er en husvegg. */
const zoomSchema = z.number().int().min(0).max(22);

export const mapPointColourSchema = z.enum(ACCREDITATION_COLOURS);

/**
 * ET PUNKT, SLIK KARTET TEGNER DET.
 *
 * `label` ER LØST OPP I SPØRRINGEN og kan likevel være null — samme situasjon `TaskLink` har, og av
 * den samme grunnen: `map_points` har ingen fremmednøkkel på `objectId`, så en slettet ressurs
 * etterlater et punkt som ikke løser opp. Forskjellen fra en chip er at et kart IKKE tegner det:
 * en navnløs prikk på et områdekart er en prikk ingen kan forklare, og lesebanen hopper over den.
 *
 * `secondary` er linja under i popup-en — typen på en ressurs, statusen på en oppgave.
 */
export const mapPointDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    objectType: mapObjectTypeSchema,
    /** Null bare for `custom`. */
    objectId: z.uuid().nullable(),
    label: z.string().nullable(),
    secondary: z.string().nullable(),
    latitude: z.string(),
    longitude: z.string(),
    zone: z.string().nullable(),
    floor: z.string().nullable(),
    icon: z.string().nullable(),
    colour: mapPointColourSchema.nullable(),
    /**
     * Avdelingen objektet hører til, når det har en. Kartet fargelegger etter den — ordren:
     * «Punkter fargelegges etter avdeling» — og `colour` overstyrer når noen har valgt en.
     */
    departmentId: z.uuid().nullable(),
    /** Oppgavens status, for de punktene som ER oppgaver. «Kun åpne» filtrerer på den. */
    status: z.string().nullable(),
  })
  .meta({ id: 'MapPoint' });

export const mapPointListResponseSchema = z
  .object({ items: z.array(mapPointDtoSchema) })
  .meta({ id: 'MapPointListResponse' });

/**
 * Hvilke lag kartsiden ber om.
 *
 * IKKE PAGET, og det er et valg med et tak bak seg: et områdekart med mer enn noen hundre punkter
 * er ikke et kart noen leser, og en markør-klynge er en annen ordre. Tjenesten har en `limit` og
 * svaret sier fra når den slo inn.
 */
export const listMapPointsQuerySchema = z
  .object({
    /** `?types=stage,resource`. Utelatt: alle lagene. */
    types: z
      .preprocess((value) => {
        if (value === undefined || value === null) return undefined;
        if (Array.isArray(value)) return value;
        const text = String(value).trim();
        return text.length === 0 ? undefined : text.split(',');
      }, z.array(mapObjectTypeSchema).min(1).max(MAP_OBJECT_TYPES.length).optional())
      .optional(),
  })
  .meta({ id: 'ListMapPointsQuery' });

/**
 * SETT ET STED PÅ ET OBJEKT — `PUT /v1/map-points/:objectType/:objectId`.
 *
 * `PUT` og ikke `POST`, fordi det er idempotent: ett punkt per objekt per årgang, og å sette det
 * igjen er å flytte det. Klienten trenger ikke vite om punktet fantes fra før, og to personer som
 * setter stedet samtidig får det samme resultatet uansett rekkefølge.
 */
export const setMapPointRequestSchema = z
  .object({
    editionId: z.uuid(),
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    zone: z.string().trim().max(80).nullish(),
    floor: z.string().trim().max(40).nullish(),
    icon: z.string().trim().max(40).nullish(),
    colour: mapPointColourSchema.nullish(),
    /**
     * Bare for `custom`. På et punkt som står PÅ noe er etiketten objektets eget navn, og en
     * overstyring her ville vært et andre navn på den samme tingen — som er hvordan et kart og en
     * liste begynner å si forskjellige ting.
     */
    label: z.string().trim().max(120).nullish(),
  })
  .meta({ id: 'SetMapPointRequest' });

/** «Legg til punkt» — et eget punkt, som ikke står på noe. */
export const createMapPointRequestSchema = z
  .object({
    editionId: z.uuid(),
    label: z.string().trim().min(1).max(120),
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    zone: z.string().trim().max(80).nullish(),
    floor: z.string().trim().max(40).nullish(),
    icon: z.string().trim().max(40).nullish(),
    colour: mapPointColourSchema.nullish(),
  })
  .meta({ id: 'CreateMapPointRequest' });

/**
 * Gi et eget punkt nytt navn, eller flytt det (QA5 C-M3). Punktet sto fast fra det ble lagt til —
 * ingen knapp kunne endre eller slette det. Minst ett felt; et tomt kall er ikke en endring.
 */
export const updateMapPointRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(120).optional(),
    latitude: latitudeSchema.optional(),
    longitude: longitudeSchema.optional(),
  })
  .refine((body) => (body.latitude === undefined) === (body.longitude === undefined), {
    error: 'both_coordinates',
    path: ['longitude'],
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    error: 'empty_update',
  })
  .meta({ id: 'UpdateMapPointRequest' });
export type UpdateMapPointRequest = z.infer<typeof updateMapPointRequestSchema>;

/**
 * ÅRGANGENS OMRÅDE: senter, zoom og områdeplanen.
 *
 * `overlayUrl` er en NEDLASTINGSLENKE og ikke en lagringsnøkkel — den samme ruta hvert annet
 * vedlegg serveres fra. Klienten skal aldri kjenne hvor bytene ligger.
 */
export const editionMapDtoSchema = z
  .object({
    editionId: z.uuid(),
    centerLat: z.string().nullable(),
    centerLng: z.string().nullable(),
    zoom: z.number().int().nullable(),
    overlayAttachmentId: z.uuid().nullable(),
    overlayUrl: z.string().nullable(),
    overlayBounds: z
      .object({
        south: z.number(),
        west: z.number(),
        north: z.number(),
        east: z.number(),
      })
      .nullable(),
    /**
     * FESTIVALENS STED (239.18). Når verken årgangen eller festivalen har et senter, åpner kartet
     * på festivalens by framfor reserven i Bodø. Klienten slår opp byen én gang og husker svaret.
     */
    festivalCity: z.string().nullable(),
    festivalCountryCode: z.string(),
  })
  .meta({ id: 'EditionMap' });

export const updateEditionMapRequestSchema = z
  .object({
    centerLat: latitudeSchema.nullish(),
    centerLng: longitudeSchema.nullish(),
    zoom: zoomSchema.nullish(),
    overlayAttachmentId: z.uuid().nullish(),
    overlayBounds: z
      .object({
        south: z.number().min(-90).max(90),
        west: z.number().min(-180).max(180),
        north: z.number().min(-90).max(90),
        east: z.number().min(-180).max(180),
      })
      .nullish(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field must be present.',
  })
  /*
   * ET SENTER ER TO TALL ELLER INGEN, og den samme skranken står i basen. Her fordi en 400 som
   * navngir feltet er et bedre svar enn en skrankekrenkelse som blir en 503.
   */
  .refine((body) => (body.centerLat == null) === (body.centerLng == null), {
    message: 'A centre is two numbers or none.',
    path: ['centerLng'],
  })
  .refine(
    (body) =>
      body.overlayBounds == null ||
      (body.overlayBounds.north > body.overlayBounds.south &&
        body.overlayBounds.east > body.overlayBounds.west),
    { message: 'North must be north of south, and east east of west.', path: ['overlayBounds'] },
  )
  .meta({ id: 'UpdateEditionMapRequest' });

export type MapPointDto = z.infer<typeof mapPointDtoSchema>;
export type MapPointListResponse = z.infer<typeof mapPointListResponseSchema>;
export type ListMapPointsQuery = z.infer<typeof listMapPointsQuerySchema>;
export type SetMapPointRequest = z.infer<typeof setMapPointRequestSchema>;
export type CreateMapPointRequest = z.infer<typeof createMapPointRequestSchema>;
export type EditionMapDto = z.infer<typeof editionMapDtoSchema>;
export type UpdateEditionMapRequest = z.infer<typeof updateEditionMapRequestSchema>;
export type MapPointColour = z.infer<typeof mapPointColourSchema>;
